import { useCallback,useMemo } from 'react';

import { useSelector } from 'react-redux';

import { collection, orderBy, query, where } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import {
  ALLOWED_ROLES,
  getDivsWithAccess,
  resolveOrgAccess,
  useFirestoreQueryBatchedPagination,
  useFirestoreQueryPagination,
} from '@src/packages/utils';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Hook to fetch groups with infinite pagination to use in a Select
 * Groups are fetched from division level: organizations/{orgId}/divisions/{divId}/groups
 * If divIds is not provided, it will use all allowed divisions for the user
 *
 * @param {Object} options - Configuration options
 * @param {string} [options.orgId] - Organization ID. If not provided, it will be obtained from Redux
 * @param {string|string[]} [options.divIds] - Division ID(s). If not provided, uses all allowed divisions
 * @param {number} [options.pageSize=20] - Page size for pagination
 * @param {Array} [options.whereConditions=[]] - Array of Firestore where conditions
 * @param {Object} [options.orderByConfig] - Ordering configuration { field: 'name', direction: 'asc' }
 * @param {string} [options.allowedRole] - Role to check for division access (default: GROUPS.LIST)
 * @returns {Object} - { data, loading, handlePopupScroll, groups }
 */
const useSelectGroupsWithPagination = ({
  orgId: providedOrgId,
  divIds = null,
  pageSize = DEFAULT_PAGE_SIZE,
  whereConditions = [],
  orderByConfig = { field: 'name', direction: 'asc' },
  allowedRole = ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.GROUPS.LIST,
} = {}) => {
  const orgIdFromRedux = useSelector(state => state.organizations.organization.id);
  const orgId = providedOrgId || orgIdFromRedux;

  // Get user access information for divisions
  const claims = useSelector(({ user }) => user.access.data?.claims);
  const orgUserAccess = useSelector(({ user }) => user.orgUser?.data?.access);
  const userOrgAccess = resolveOrgAccess(claims, orgId, orgUserAccess);
  const userOrgDivisions = useSelector(({ divisions }) => divisions.assigned.data.divisions);

  // Get allowed divisions if divIds is not provided
  const allowedDivs = useMemo(() => {
    return getDivsWithAccess(allowedRole, userOrgAccess, userOrgDivisions);
  }, [allowedRole, userOrgAccess, userOrgDivisions]);

  const allowedDivsIds = useMemo(() => {
    return allowedDivs.map(d => d.id);
  }, [allowedDivs]);

  // Determine which divisions to use
  const divIdsArray = useMemo(() => {
    if (divIds) {
      return Array.isArray(divIds) ? divIds : [divIds];
    }
    // If no divIds provided, use all allowed divisions
    return allowedDivsIds;
  }, [allowedDivsIds, divIds]);

  const useMultipleQueries = divIdsArray && divIdsArray.length > 1;

  // Memoize orderByConfig to prevent reference changes
  const memoizedOrderByConfig = useMemo(
    () => orderByConfig,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderByConfig?.field, orderByConfig?.direction],
  );

  // Memoize whereConditions - user should memoize them in component if they need to change
  const memoizedWhereConditions = useMemo(
    () => whereConditions,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [whereConditions?.length],
  );

  // Build query(ies) for groups
  const groupsQueryRef = useMemo(() => {
    if (!orgId || divIdsArray.length === 0) {
      return query(
        collection(db, FSCollections.ORGANIZATIONS, 'temp', FSCollections.GROUPS),
        where('id', '==', 'never-exists'),
      );
    }

    // Case 1: Single division
    if (divIdsArray.length === 1) {
      const queryParts = [
        collection(db, FSCollections.ORGANIZATIONS, orgId, FSCollections.DIVISIONS, divIdsArray[0], FSCollections.GROUPS),
        ...memoizedWhereConditions,
        orderBy(memoizedOrderByConfig.field, memoizedOrderByConfig.direction),
      ];

      return query(...queryParts);
    }

    // Case 2: Multiple divisions - return array of queries
    return divIdsArray.map(divId => {
      const queryParts = [
        collection(db, FSCollections.ORGANIZATIONS, orgId, FSCollections.DIVISIONS, divId, FSCollections.GROUPS),
        ...memoizedWhereConditions,
        orderBy(memoizedOrderByConfig.field, memoizedOrderByConfig.direction),
      ];

      return query(...queryParts);
    });
  }, [orgId, divIdsArray, memoizedWhereConditions, memoizedOrderByConfig]);

  // Create stable dependencies array - use serializable values only
  const dependencies = useMemo(() => {
    const deps = [orgId, pageSize];

    if (divIdsArray.length > 0) {
      deps.push(JSON.stringify(divIdsArray));
    }

    deps.push(memoizedOrderByConfig.field, memoizedOrderByConfig.direction);
    deps.push(memoizedWhereConditions.length);

    return deps;
  }, [
    orgId,
    divIdsArray,
    memoizedOrderByConfig.field,
    memoizedOrderByConfig.direction,
    memoizedWhereConditions.length,
    pageSize,
  ]);

  // Hook for a single query
  const singleQueryResult = useFirestoreQueryPagination(
    !useMultipleQueries ? groupsQueryRef : [],
    pageSize,
    dependencies,
  );

  // Hook for multiple queries
  const multipleQueriesResult = useFirestoreQueryBatchedPagination(
    useMultipleQueries ? groupsQueryRef : [],
    dependencies,
    pageSize,
  );
  // Select the correct result
  const {
    data: groups = [],
    loading,
    next,
    loaded,
  } = useMultipleQueries ? multipleQueriesResult : singleQueryResult;

  // Transform groups to options for the Select
  const data = useMemo(() => {
    // Remove duplicates in case a group appears in multiple divisions
    const uniqueGroups = groups.filter(
      (group, index, self) => index === self.findIndex(g => g.id === group.id),
    );

    return uniqueGroups.map(group => ({
      label: group.name,
      value: group.id,
    }));
  }, [groups]);

  // Handler for infinite scroll
  const handlePopupScroll = useCallback(
    e => {
      const { target } = e;
      if (
        !loading &&
        loaded &&
        next &&
        target.scrollTop + target.offsetHeight >= target.scrollHeight - pageSize
      ) {
        next();
      }
    },
    [loading, loaded, next, pageSize],
  );

  return {
    data,
    loading,
    handlePopupScroll,
    groups,
  };
};

export default useSelectGroupsWithPagination;
